<!--
  대상자 1명의 계약 조건 입력 (2026-09-12) — 양식 term_fields대로 입력칸을 만들고 set-terms API로 저장.
  본문 서식이 있는 양식이면 저장 직후 서버가 계약서 PDF를 자동 생성한다.
-->
<template>
  <el-dialog v-model="visible" :title="`계약 조건 입력 — ${partyName}`" width="620px" destroy-on-close>
    <el-alert v-if="fields.length === 0" type="info" :closable="false" show-icon>
      이 양식에는 계약 조건 항목이 없습니다. 양식 관리 &gt; ⑤ 계약 조건 항목에서 먼저 정의하세요.
    </el-alert>
    <el-form v-else label-width="140px" label-position="right">
      <el-form-item v-for="f in fields" :key="f.key" :label="f.label" :required="f.required">
        <el-date-picker v-if="f.type === 'date'" v-model="form[f.key]" type="date" value-format="YYYY-MM-DD" placeholder="YYYY-MM-DD" style="width:100%" />
        <el-select v-else-if="f.type === 'select'" v-model="form[f.key]" clearable style="width:100%">
          <el-option v-for="o in f.options" :key="o" :label="o" :value="o" />
        </el-select>
        <el-input v-else-if="f.type === 'money' || f.type === 'number'" v-model="form[f.key]" :placeholder="f.type === 'money' ? '숫자 (예: 36000000)' : '숫자'" style="width:100%">
          <template #append>{{ f.unit || (f.type === 'money' ? '원' : '') }}</template>
        </el-input>
        <el-input v-else v-model="form[f.key]" :placeholder="f.default || ''" style="width:100%" />
        <div v-if="f.help" class="hint">{{ f.help }}</div>
        <div v-if="f.type === 'money' && form[f.key]" class="hint">= {{ formatMoney(form[f.key]) }}{{ f.unit || '원' }}</div>
      </el-form-item>
    </el-form>
    <el-alert v-if="errors.length" type="error" :closable="false" style="margin-top:8px">
      <ul style="margin:0;padding-left:16px"><li v-for="e in errors" :key="e">{{ e }}</li></ul>
    </el-alert>
    <template #footer>
      <el-button @click="visible = false">취소</el-button>
      <el-button type="primary" :loading="saving" :disabled="fields.length === 0" @click="save">
        저장{{ hasBody ? ' 후 계약서 생성' : '' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { ElMessage } from "element-plus";
import { useContractStore } from "@/stores/contractStore";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  contractId: { type: [Number, String], required: true },
  party: { type: Object, default: null },
  fields: { type: Array, default: () => [] },
  hasBody: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue", "saved"]);
const store = useContractStore();

const visible = computed({ get: () => props.modelValue, set: (v) => emit("update:modelValue", v) });
const partyName = computed(() => props.party?.employee_name || props.party?.employee_username || "");
const form = ref({});
const errors = ref([]);
const saving = ref(false);

watch(visible, (v) => {
  if (!v) return;
  errors.value = [];
  const init = {};
  for (const f of props.fields) {
    const cur = props.party?.terms?.[f.key];
    init[f.key] = cur !== undefined && cur !== null && cur !== "" ? cur : (f.default || "");
  }
  form.value = init;
});

function formatMoney(v) {
  const n = Number(String(v).replace(/[,\s원]/g, ""));
  return Number.isFinite(n) ? n.toLocaleString() : v;
}

async function save() {
  saving.value = true; errors.value = [];
  try {
    const res = await store.setPartyTerms(props.contractId, props.party.id, form.value);
    ElMessage.success(res.message);
    if (res.generate_errors?.length) ElMessage.warning("문서 생성 실패: " + res.generate_errors.join(", "));
    emit("saved");
    visible.value = false;
  } catch (e) {
    errors.value = e.response?.data?.errors || [e.response?.data?.error || e.message];
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped>
.hint { font-size: var(--sw-font-xs); color: var(--el-text-color-secondary); width: 100%; }
</style>
